import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axiosBetterStacktrace from 'axios-better-stacktrace';
import { APIError, JSONResponse } from './APIError';
import { formatErrorClassName } from '../utils';
import { NetworkErrorReporterParams } from '../types';

interface HTTPClientArgs {
	/**
	 * Function to report errors. If not provided, errors will be logged to the console.
	 * Should be a return value of buildNetworkErrorReporter.
	 *
	 * @memberof HTTPClientArgs
	 */
	errorReporter?: (args: NetworkErrorReporterParams) => void;
	/**
	 * Name of the JWT token in the session storage. If not provided Authorization header will not be added automatically.
	 *
	 * @type {string}
	 * @memberof HTTPClientArgs
	 */
	authTokenName?: string;
	/**
	 * If true, all requests and responses will be logged to the console.
	 *
	 * @type {boolean}
	 * @memberof HTTPClientArgs
	 */
	debug?: boolean;
}

type ExtendedAxiosRequestConfig = AxiosRequestConfig & { requestName: string; url: string; errorMessage?: string };

export type InterceptorErrorArgs = AxiosError | Error | null;
export type ErrorDetails = {
	response: AxiosResponse | null | undefined;
	request: AxiosRequestConfig | null | undefined;
	status: number | null | undefined;
};

type FileInfo = {
	name: string;
	type: string;
	size: number;
};

function logInfo(message: string, ...args: unknown[]) {
	console.info(`[HTTPClient] ${message}`, ...args);
}

class HTTPClient {
	private debug = false;
	private _authTokenName: string | null = null;
	private instance: AxiosInstance | null = null;
	private errorReporter: null | ((args: NetworkErrorReporterParams) => void) = null;
	private _headers: Readonly<Record<string, string | boolean>> = {
		Accept: 'application/json',
		'Content-Type': 'application/json; charset=utf-8',
		'Access-Control-Allow-Credentials': true,
	};

	/**
	 * Creates an instance of HTTPClient.
	 * @param {HTTPClientArgs} [args]
	 * @memberof HTTPClient
	 */

	constructor(args?: HTTPClientArgs) {
		const http = axios.create({
			headers: this._headers,
			withCredentials: true,
		});

		axiosBetterStacktrace(http);

		if (args?.errorReporter) {
			this.errorReporter = args.errorReporter;
		}

		if (args?.authTokenName) {
			this._authTokenName = args.authTokenName;
		}

		if (args?.debug) {
			this.debug = args.debug;
		}

		http.interceptors.request.use(this.injectToken.bind(this), (error) => Promise.reject(error));

		http.interceptors.response.use(
			(response) => response,
			(error) => this.handleError(error)
		);

		this.instance = http;
	}

	get authTokenName(): string | null {
		return this._authTokenName;
	}

	set authTokenName(value: string | null) {
		this._authTokenName = value;
	}

	/**
	 * Injects the JWT token as Authorization header through an interceptor if token name is specified.
	 * Token is being retrieved from the session storage.
	 *
	 * @private
	 * @param {InternalAxiosRequestConfig} config
	 * @return {*}  {InternalAxiosRequestConfig}
	 * @memberof HTTPClient
	 */
	private injectToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
		if (this._authTokenName) {
			try {
				const token = globalThis.sessionStorage.getItem(this._authTokenName);

				let parsedToken = null;

				try {
					parsedToken = JSON.parse(token as string);
				} catch (error) {
					parsedToken = token;
				}

				if (token !== null) {
					config.headers.Authorization = `Bearer ${parsedToken}`;
				}
			} catch (error) {
				logInfo('Error while injecting token: ', error);
				throw new Error(error as string);
			}
		}

		return config;
	}

	/**
	 * Parses the request body for error reporting
	 *
	 * @private
	 * @param {unknown} body
	 * @return {*}
	 * @memberof HTTPClient
	 */
	private parseRequestBody(body: unknown) {
		if (body === null || body === undefined) {
			return body;
		}

		if (typeof body === 'string') {
			let parsedBody = '';

			try {
				parsedBody = JSON.parse(body);
			} catch {
				parsedBody = body;
			}

			return parsedBody;
		}

		if (typeof body === 'object') {
			if (body instanceof FormData) {
				const parsedBody: Record<string, FormDataEntryValue | FileInfo> = {};
				for (const entry of body.entries()) {
					const [key, value] = entry;
					parsedBody[key] =
						value instanceof File
							? {
									name: value.name,
									size: value.size,
									type: value.type,
								}
							: value;
				}

				return parsedBody;
			}

			return body;
		}

		return `Unexpected request body format! type: ${typeof body}, toString: ${body}`;
	}

	/**
	 * Handles the error response from the API. If error reporter is specified, it will be called.
	 * Otherwise, it will return a rejected promise with the error.
	 * The error reporter should be a function generated via buildNetworkErrorReporter.
	 *
	 * @private
	 * @param {AxiosError} error
	 * @param {ExtendedAxiosRequestConfig} request
	 * @return {*}
	 * @memberof HTTPClient
	 */
	private handleError(error: InterceptorErrorArgs) {
		const { response, config } = Object(error) as AxiosError;

		if (this.errorReporter !== null) {
			this.errorReporter({
				response,
				error,
				errorName: (config as ExtendedAxiosRequestConfig)?.requestName as string,
				responseBody: response?.data as Record<string, unknown>,
				requestBody: this.parseRequestBody(config?.data),
			});
		}

		return Promise.reject(error);
	}

	/**
	 * Updates the default headers for all requests.
	 *
	 * @param {(Readonly<Record<string, string | boolean>>)} additionalHeaders
	 * @memberof HTTPClient
	 */
	updateDefaultHeaders(additionalHeaders: Readonly<Record<string, string | boolean>>) {
		if (!this.instance) return;

		for (const [key, value] of Object.entries(additionalHeaders)) {
			this.instance.defaults.headers[key] = value;
		}
	}

	/**
	 * Attaches a request interceptor to the Axios HTTPClient instance.
	 *
	 * Documentation: _[Axios Interceptors](https://axios-http.com/docs/interceptors)_
	 *
	 * The handler function **must return the config object**, otherwise the request will hang.
	 *
	 * @param {(config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig} handler
	 * @return {number} - The interceptor ID
	 * @memberof HTTPClient
	 */
	attachRequestInterceptor(handler: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig) {
		return this.instance?.interceptors.request.use(handler, undefined);
	}

	/**
	 * Detaches a request interceptor from the Axios HTTPClient instance.
	 *
	 * @param {number} handlerId
	 * @memberof HTTPClient
	 */
	detachRequestInterceptor(handlerId: number) {
		this.instance?.interceptors.request.eject(handlerId);
	}

	/**
	 * Attaches a response error interceptor to the Axios HTTPClient instance.
	 *
	 * Documentation: _[Axios Interceptors](https://axios-http.com/docs/interceptors)_
	 *
	 * The handler function **must Promise.reject the error object**, otherwise the error will not be passed to other error interceptors.
	 *
	 * @param {(error: InterceptorErrorArgs) => Promise<InterceptorErrorArgs>} handler
	 * @return {number} - The interceptor ID
	 * @memberof HTTPClient
	 */
	attachResponseErrorInterceptor(handler: (error: InterceptorErrorArgs) => Promise<InterceptorErrorArgs>) {
		return this.instance?.interceptors.response.use(undefined, handler);
	}

	/**
	 * Attaches a response success interceptor to the Axios HTTPClient instance.
	 *
	 * Documentation: _[Axios Interceptors](https://axios-http.com/docs/interceptors)_
	 *
	 * The handler function **must return the response object**, otherwise the request will hang.
	 *
	 * @param {(response: AxiosResponse) => Promise<AxiosResponse>} handler
	 * @return {number} - The interceptor ID
	 * @memberof HTTPClient
	 */
	attachResponseSuccessInterceptor(handler: (response: AxiosResponse) => AxiosResponse) {
		return this.instance?.interceptors.response.use(handler, undefined);
	}

	/**
	 * Detaches a response interceptor from the Axios HTTPClient instance.
	 *
	 * @param {number} handlerId
	 * @memberof HTTPClient
	 */
	detachResponseInterceptor(handlerId: number) {
		this.instance?.interceptors.response.eject(handlerId);
	}

	resolveErrorDetails(error: InterceptorErrorArgs): ErrorDetails {
		const details: ErrorDetails = {
			response: null,
			request: null,
			status: -999,
		};

		if (axios.isAxiosError(error)) {
			const { status } = (error.response as AxiosResponse) ?? {};

			details.response = error.response;
			details.request = error.config;
			details.status = status;
		}

		return details;
	}

	/**
	 * Returns the Axios HTTPClient instance.
	 *
	 * @return {HTTPClient}
	 * @memberof HTTPClient
	 */
	getHTTPClient() {
		if (!this.instance) throw new Error('HTTPClient instance is not initialized');

		return this.instance;
	}

	/**
	 * Sends a axios request to the specified URL. If no method is specified, it will default to GET.
	 *
	 * @template T - The type of the response data
	 * @param {ExtendedAxiosRequestConfig} config - The request configuration _[AxiosRequestConfig](https://axios-http.com/docs/req_config)_
	 * @return {Promise<T>}
	 * @memberof HTTPClient
	 */
	async request<T = unknown>(config: ExtendedAxiosRequestConfig) {
		if (!this.instance) throw new Error('HTTPClient instance is not initialized');

		return this.instance
			.request(config)
			.then((res) => res.data as T)
			.catch((error: AxiosError) => {
				if (error?.response) {
					if (this.debug) {
						logInfo('Error response - Request Error: ', {
							response: error.response.data,
							status: error.response.status,
							headers: error.response.headers,
						});
					}

					throw new APIError({
						url: config.url,
						name: formatErrorClassName(config.requestName),
						message: error?.message || config.errorMessage || 'HTTPClient: Request Error',
						statusCode: error.response.status,
						responseBody: error.response.data as JSONResponse,
						requestBody: config.data,
						responseHeaders: error.response.headers,
					});
				} else if (error?.request) {
					// The request was made but no response was received
					// `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
					if (this.debug) {
						logInfo('Error response - Request Timeout: ', {
							status: error.status,
							request: error.request,
						});
					}

					throw new APIError({
						url: config.url,
						name: formatErrorClassName(config.requestName),
						message: config.errorMessage || 'HTTPClient: Request Timeout',
						requestBody: config.data,
					});
				}

				if (this.debug) {
					logInfo('Error response - Unknown Error: ', error);
				}

				throw new APIError({
					url: config.url,
					name: formatErrorClassName(config.requestName),
					message: config.errorMessage || 'HTTPClient: Unknown Error',
					requestBody: config.data,
				});
			});
	}
}

export { HTTPClient };
